/**
 * OS owner-facing status copy (PRD-004 §4 PR-OS-01; final design pass).
 *
 * The single highest-leverage piece of the OS concierge (DELIVERY.md): the
 * status line and supporting subtitle shown at the top of the owner's surface.
 * Copy is owner-facing, first person, Eric's warm voice. No em dashes (the CI
 * em-dash gate runs over this string set). No countdown or deadline pressure
 * semantics: this is the two-session spine, not the 72h ClockBar.
 *
 * Canonical copy: the strings below are adopted verbatim from the Claude Design
 * OS surface (design-final/Studio OS.html STAGES array), which is Eric-reviewed.
 * Each os_status maps to a `line` (the big serif status), a `sub` (the calm
 * supporting paragraph), and an optional `cta` label. The scope/date tokens are
 * operator-supplied per engagement (`scope`, `ready_by`, the `session*_booked_at`
 * timestamps); when a token is missing we degrade gracefully to an on-voice line
 * that does not show an empty bracket or a broken date.
 */

import type { OsStatus, OwnOsEngagement } from '@/lib/os';

/** The line + subtitle (+ optional CTA label) shown for one os_status. */
export interface OsStatusCopy {
  line: string;
  sub: string;
  cta?: string;
}

/**
 * Format a timestamp (or date) column into a calm, human date. Returns null
 * when the value is absent or unparseable so callers can fall back. No time of
 * day: the OS spine is about the day, not a deadline.
 */
function formatDate(value: string | null): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * The owner-facing status copy for the engagement's current `os_status`.
 *
 * `intake` has no live surface in the lifecycle table (the surface goes live at
 * `acknowledged`); if an engagement is somehow viewed at `intake` we render a
 * gentle holding line rather than a blank, on-voice and without time pressure.
 */
export function osStatusCopy(engagement: OwnOsEngagement): OsStatusCopy {
  const status: OsStatus = engagement.status;
  switch (status) {
    case 'intake':
      return {
        line: "I've got your request. I'll be in touch shortly.",
        sub: "Nothing for you to do yet. I'll write the moment there's something to see.",
      };

    case 'acknowledged':
      return {
        line: "On my desk. I'm reading your situation now.",
        sub: "Nothing for you to do yet. I'll write the moment there's something to see.",
      };

    case 'scoped': {
      const x = engagement.scope?.trim();
      const line = x
        ? `Scoped. I think the place to start is ${x}.`
        : "Scoped. I think the place to start is the loop between the walk where you think and the desk where you'd write.";
      return {
        line,
        sub: "Have a read of where I'd start. When it feels right, pick a time and we'll begin.",
        cta: 'Book session one',
      };
    }

    case 'session1_booked': {
      const date = formatDate(engagement.session1_booked_at);
      return {
        line: date ? `We're booked. See you ${date}.` : "We're booked. See you soon.",
        sub: "I've blocked the time. Bring the workflow that's been quietly costing you. Nothing to prepare.",
      };
    }

    case 'discovery_done':
      return {
        line: "Good session. I have what I need. From here it's on me for a few days.",
        sub: "This next part is quiet on your side. That's normal, and it's the point.",
      };

    case 'building': {
      const date = formatDate(engagement.ready_by);
      const line = date
        ? `I'm building your system. Ready by ${date}.`
        : "I'm building your system. I'll let you know the moment it's ready.";
      return {
        line,
        sub: "The quiet part. I'll surface again the moment there's something for you to watch.",
      };
    }

    case 'handoff_ready':
      return {
        line: "Your system's ready. Watch the short walkthrough, then let's install it together.",
        sub: "Take six minutes with the walkthrough below whenever you like, then we'll set it up live.",
      };

    case 'session2_booked': {
      const date = formatDate(engagement.session2_booked_at);
      return {
        line: date ? `Install booked for ${date}.` : 'Install booked. See you soon.',
        sub: "We'll set it up on your machine and make sure it's truly yours before we close.",
      };
    }

    case 'installed':
      return {
        line: "Installed. It's yours.",
        sub: "It's running now. Everything below is yours to keep, run, and rebuild.",
      };

    case 'open':
      return {
        line: "Your studio's open. Here's what we could do next.",
        sub: "No pressure and no clock. Whenever you want a hand with the next one, the door's open.",
      };

    default: {
      // Exhaustiveness guard: if a new os_status value is added without copy,
      // the never-assignment fails typecheck rather than rendering a blank.
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}
